import React, { useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import {
  AppBar,
  Badge,
  Grid,
  Hidden,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import InputIcon from "@material-ui/icons/Input";
import MenuIcon from "@material-ui/icons/Menu";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import NotificationsIcon from "@material-ui/icons/Notifications";
import SettingsIcon from "@material-ui/icons/Settings";

import { Theme } from "src/theme";
import { useThemeContext } from "src/contexts/ThemeContext";
import { AdapterLink } from "src/utils";
import { setActiveDialog, setWalkthroughId } from "src/store/adminHtml/actions";
import {
  activeDialogSelector,
  alertsSelector,
} from "src/store/adminHtml/selector";
import { isLoggedInSelector } from "src/store/system/selector";
import { openZendeskWithSSO, ZENDESK_BASE_URL } from "src/utils/zendeskSso";
import InfoTooltip from "../InfoTooltip";
import NotificationsDialog from "./NotificationsDialog";
import ProfileSettingsDialog from "src/pages/RootDialogs/ProfileSettings";
import { getIsEbayImport } from "src/store/sale/selector";

import type { ActiveDialog } from "src/interfaces/adminHtmlState.interface";
import { useRecordEvent } from "src/hooks/useRecordEvent";

const useStyles = makeStyles((theme: Theme) => {
  const { derived } = theme.palette.custom;

  return {
    appBar: {
      backgroundColor: "transparent",
      color: derived.titleOnBg,
    },
    menuButton: {
      marginLeft: -theme.spacing(1),
      color: derived.titleOnBg,
    },
    titleContainer: {
      display: "flex",
      flexDirection: "column",
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    title: {
      color: derived.titleOnBg,
      fontWeight: theme.typography.fontWeightBold,
    },
    subtitle: {
      color: derived.titleOnBg,
    },
    iconButton: {
      color: derived.titleOnBg,
      transition: "transform 0.2s ease",
      "&:hover": {
        transform: "scale(1.15)",
        backgroundColor: "transparent",
      },
    },
  };
});

export const AdminHeader = (props: any) => {
  const recordEvent = useRecordEvent();

  const dispatch = useDispatch();
  const store = useStore();
  const classes = useStyles();
  const loggedIn = useSelector(isLoggedInSelector);
  const isEbayImport = useSelector(getIsEbayImport);
  const [menuAnchor, setMenuAnchor] = useState<any>(null);
  // const [helpMenuAnchor, setHelpMenuAnchor] = useState<any>(null);

  const alerts = useSelector(alertsSelector);
  const activeDialog = useSelector(activeDialogSelector);
  const onItemClick = (id: ActiveDialog) => dispatch(setActiveDialog(id));

  const openMenu = (e) => {
    setMenuAnchor(e.currentTarget);
    recordEvent({
      type: "feature-adoption",
      feature: "navigation-selection",
      element: "profile-icon",
    });
  };
  // const openHelpMenu = (e: any) => setHelpMenuAnchor(e.currentTarget);

  const closeMenu = () => setMenuAnchor(null);
  // const closeHelpMenu = () => setHelpMenuAnchor(null);

  const openUploadHistory = () => {
    closeMenu();
    onItemClick("manage_uploads");
  };

  const openProfileSettings = () => {
    closeMenu();
    props.setDialog("settings");
    recordEvent({
      type: "feature-adoption",
      feature: "navigation-selection",
      element: "profile-settings-icon",
    });
  };

  const closeDialog = () => {
    props.setDialog(undefined);
  };

  const closeNotificationsDialog = () => {
    if (activeDialogSelector(store.getState() as any) === "notifications") {
      dispatch(setActiveDialog(""));
    }
  };

  const openWalkthroughDialog = () => {
    if (props.walkthroughId) {
      dispatch(setActiveDialog("walkthrough"));
      dispatch(setWalkthroughId(props.walkthroughId));
    }
  };

  const openNotificationsDialog = () => {
    recordEvent({
      type: "feature-adoption",
      feature: "navigation-selection",
      element: "notifications-icon",
    });
    dispatch(setActiveDialog("notifications"));
  };

  const { toggleMode, isDark } = useThemeContext();

  const handleThemeToggle = () => {
    toggleMode();
    recordEvent({
      type: "feature-adoption",
      feature: "theme-mode-toggle",
      element: "header-toggle",
      mode: isDark ? "light" : "dark",
    });
  };

  const renderUserBar = () => {
    if (loggedIn) {
      return (
        <React.Fragment>
          <Grid item>
            <Tooltip
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              <IconButton
                className={classes.iconButton}
                onClick={handleThemeToggle}
                aria-label="Toggle light/dark mode"
              >
                {isDark ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <IconButton
              id="notifications-icon"
              className={classes.iconButton}
              onClick={openNotificationsDialog}
              disabled={isEbayImport}
            >
              <Badge
                invisible={!alerts.length}
                badgeContent={alerts.length}
                color="error"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton
              className={classes.iconButton}
              onClick={openProfileSettings}
            >
              <SettingsIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton className={classes.iconButton} onClick={openMenu}>
              <AccountCircleIcon fontSize="large" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              keepMounted
              open={!!menuAnchor}
              onClose={closeMenu}
            >
              <MenuItem component={AdapterLink} href="#" to="/logout">
                Log out
              </MenuItem>
              <MenuItem onClick={openUploadHistory}>Upload history</MenuItem>
              <MenuItem
                onClick={() => {
                  openZendeskWithSSO(`${ZENDESK_BASE_URL}/hc/en-us`);
                }}
              >
                Help Center
              </MenuItem>
            </Menu>
          </Grid>
          <ProfileSettingsDialog
            open={props.dialog === "settings"}
            onClose={closeDialog}
          />
          <NotificationsDialog
            items={alerts}
            open={activeDialog === "notifications"}
            onClose={closeNotificationsDialog}
          />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <Grid item>
          <Tooltip title="Log In">
            <IconButton
              className={classes.iconButton}
              component={AdapterLink}
              to="/login"
              href="#"
            >
              <InputIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Grid>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <AppBar className={classes.appBar} position="static" elevation={0}>
        <Toolbar>
          <Grid container alignItems="center">
            <Hidden smUp>
              <Grid item>
                <IconButton
                  aria-label="Open drawer"
                  onClick={props.onDrawerToggle}
                  className={classes.menuButton}
                  href="#"
                >
                  <MenuIcon />
                </IconButton>
              </Grid>
            </Hidden>
            <Grid item className={classes.titleContainer}>
              <Typography className={classes.title} variant="h5">
                {props.title}
                {!!props.walkthroughId ? (
                  <InfoTooltip text={""} onClick={openWalkthroughDialog} />
                ) : null}
              </Typography>
              {props.description && (
                <Typography className={classes.subtitle} variant="subtitle1">
                  {props.description}
                </Typography>
              )}
            </Grid>
            <Grid item xs></Grid>
            {renderUserBar()}
          </Grid>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};

export default AdminHeader;
